import AppListHeader from "@/features/ui/app-list-header";
import React, { use, useCallback, useEffect, useMemo, useState } from "react";
import useIdentity from "@/features/ui/hooks/use-identity";
import { PersistedLineSheetSetDetailPageQuery } from "@/features/line-sheet-sets/detail/use-persisted-line-sheet-sheet-set-detail-page-query";
import useDownloadUnoFileInfinite from "@/hooks/use-download-uno-file-infinite";
import {
  findYWRInfiniteSegment,
  postProcessYWRInfiniteData,
} from "@/features/ui/helpers/ywr-helpers";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { useSWRConfig } from "swr";
import {
  BoutiqueConfirmAllOrdersResponse,
  useBoutiqueConfirmAllOrders,
  useBoutiqueConfirmReadiness,
} from "@/features/line-sheet-sets/hooks/use-boutique-confirm-all-orders";
import { downloadUnoFileKey } from "@/hooks/use-download-uno-file";
import { FormikProps } from "formik";
import {
  isLineSheetFormField,
  isOrderSheetFormField,
  LineSheetSetFormFields,
} from "@/features/line-sheet-sets/detail/line-sheet-set-detail-page";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useUpdateOrderSheetSetStatusInfinite from "@/features/order-status/hooks/use-update-order-sheet-set-status-infinite";
import { KeyedMutator } from "swr";
import { GetDenseLineSheetSetResponse } from "@/features/line-sheet-sets/hooks/use-get-dense-line-sheet-set";
import { useLineSheetSetDumbTransaction } from "@/features/line-sheet-sets/hooks/use-line-sheet-set-dumb-transaction";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import useReportLineSheetSetInfinite from "@/features/line-sheet-sets/hooks/use-report-line-sheet-set-infinite";
import useReparseLineSheetSet from "@/features/line-sheet-sets/hooks/use-reparse-line-sheet-set";
import LineSheetSetReviewStatusUpdater from "@/features/line-sheet-sets/line-sheet-set-review-status-updater";
import OrderSheetStatusUpdater, {
  UpdateOrderSheetSetStatusItem,
  UpdateOrderSheetStatusItem,
} from "@/features/order-status/order-sheet-status-updater";
import useDisclosureWithData from "@/features/order-sheet-sets/hooks/use-disclosure-with-data";
import OrderOrderSheetSetModal, {
  OrderOrderSheetSetModalProps,
} from "@/features/order-sheet-sets/order-order-sheet-set-modal";
import { mutateIfMatched } from "@/features/ui/utils/swr-utils";
import useDeleteOrderSheetSet from "@/features/order-sheet-sets/hooks/use-delete-order-sheet-set";
import useAppDialog from "@/features/ui/hooks/use-app-dialog";
import AppCompanySelect from "../app-company-select";
import { toTitle } from "@/utils/case";
import { t } from "i18next";
import useTypedSearchParams from "@/features/invoices/hooks/use-typed-search-params";
import AppSeasonMultiSelect from "../app-season-multi-select";
import AppQueryController from "@/features/ui/app-query-controller";
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import SVG from "react-inlinesvg";
import ChipList from "@/features/order-sheet-sets/ChipList";
import FilterModal from "@/features/order-sheet-sets/FilterModal";
import CopyLineSheetSetModal from "@/features/line-sheet-sets/copy-line-sheet-set-modal";
import OrderRuleStatusModal from "@/features/line-sheet-sets/order-rule-status-modal";
import useImageCheckLineSheetSet from "@/features/line-sheet-sets/hooks/use-image-check-line-sheet-set";
import useUpdateOrderRuleStatus from "@/features/line-sheet-sets/hooks/use-update-order-rule-status";

interface LineSheetSetDetailPageHeaderProps {
  initialQuery: PersistedLineSheetSetDetailPageQuery;
  formik: FormikProps<LineSheetSetFormFields>;
  mutate: KeyedMutator<GetDenseLineSheetSetResponse>;
  onPublishClick: () => void;
  isEditing: boolean;
}

export default function LineSheetSetDetailPageHeader({
  initialQuery,
  formik,
  mutate,
  isEditing,
  onPublishClick,
}: LineSheetSetDetailPageHeaderProps) {
  const identity = useIdentity();
  const { error: showError, info: showInfo } = useAppToasts();
  const { tTitle, tSentence } = useI18nHelper();
  const { navigate } = useRouterHelper();
  const { openDialog } = useAppDialog();

  const emptyStackPage = useMemo(() => {
    const companyType = identity?.company?.type;
    const infer = identity?.company?.type === "BUYER";
    if (companyType === "AGENCY") {
      const isOrderSheetSet = infer || initialQuery.orderSheetSetId;
      if (isOrderSheetSet) {
        return "/order-sheets";
      } else {
        return "/line-sheets";
      }
    } else if (companyType === "BUYER") {
      return "/line-sheets";
    }
  }, [identity?.company?.type, initialQuery.orderSheetSetId]);

  const [query, setQuery] =
    useTypedSearchParams<PersistedLineSheetSetDetailPageQuery>();
  const [isQuerySet, setIsQuerySet] = useState(false);

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [queryCount, setQueryCount] = useState(0);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [isOrderRuleStatusModalOpen, setIsOrderRuleStatusModalOpen] =
    useState(false);

  useEffect(() => {
    if (!isQuerySet && !query && initialQuery) {
      setQuery(initialQuery);
      setIsQuerySet(true);
    }
  }, [initialQuery, isQuerySet, query, setQuery]);

  // API : Download Uno File
  const {
    data: downloadUnoFileData,
    fire: fireDownloadUnoFile,
    clear: clearDownloadUnoFile,
  } = useDownloadUnoFileInfinite();

  useEffect(() => {
    postProcessYWRInfiniteData(
      downloadUnoFileData,
      clearDownloadUnoFile,
      showError,
      () => {}
    );
  }, [downloadUnoFileData, clearDownloadUnoFile, showError]);

  const findDownloadUnoFileSegment = useCallback(
    (unoFileId: number) => {
      return findYWRInfiniteSegment(downloadUnoFileData, downloadUnoFileKey, {
        unoFileId: unoFileId,
      });
    },
    [downloadUnoFileData]
  );

  // END: API : Download Uno File

  // --- API: Update Order Sheet Set Status ---
  const {
    fire: fireUpdateOrderSheetSetStatus,
    data: updateOrderSheetSetStatusData,
    clear: clearUpdateOrderSheetSetStatus,
    error: updateOrderSheetSetStatusError,
  } = useUpdateOrderSheetSetStatusInfinite();

  useEffect(() => {
    if (updateOrderSheetSetStatusError) {
      showError(updateOrderSheetSetStatusError);
    }
  }, [showError, updateOrderSheetSetStatusError]);

  useEffect(() => {
    postProcessYWRInfiniteData(
      updateOrderSheetSetStatusData,
      clearUpdateOrderSheetSetStatus,
      showError,
      (response) => {
        mutate();
      }
    );
  }, [
    updateOrderSheetSetStatusData,
    clearUpdateOrderSheetSetStatus,
    showError,
    mutate,
  ]);

  // --- END: API: Update Order Sheet Set Status ---

  const {
    isLoading: isDeleteOrderSheetSetLoading,
    fire: fireDeleteOrderSheetSet,
    data: fireDeleteOrderSheetSetData,
    error: fireDeleteOrderSheetSetError,
    clear: clearDeleteOrderSheetSet,
  } = useDeleteOrderSheetSet();

  useEffect(() => {
    if (fireDeleteOrderSheetSetError) {
      showError(fireDeleteOrderSheetSetError);
    }
  }, [showError, fireDeleteOrderSheetSetError]);

  // API: Force Parsing Fail Line Sheet Set
  const {
    data: reportLineSheetSetData,
    isLoading: isReportLineSheetSetLoading,
    fire: fireReportLineSheetSet,
    clear: clearReportLineSheetSet,
    error: reportLineSheetSetError,
  } = useReportLineSheetSetInfinite();

  useEffect(() => {
    if (reportLineSheetSetError) {
      showError(reportLineSheetSetError);
    }
  }, [showError, reportLineSheetSetError]);

  useEffect(() => {
    postProcessYWRInfiniteData(
      reportLineSheetSetData,
      clearReportLineSheetSet,
      showError,
      () => {
        mutate();
      }
    );
  }, [reportLineSheetSetData, clearReportLineSheetSet, showError, mutate]);

  // END: API: Force Parsing Fail Line Sheet Set

  // API: Reparse Line Sheet Set
  const {
    fire: fireReparseLineSheetSet,
    isLoading: isReparseLineSheetSetLoading,
    data: reparseLineSheetSetData,
    error: reparseLineSheetSetError,
    clear: clearReparseLineSheetSet,
  } = useReparseLineSheetSet();

  useEffect(() => {
    if (reparseLineSheetSetError) {
      showError(reparseLineSheetSetError);
    }
  }, [showError, reparseLineSheetSetError]);

  useEffect(() => {
    if (reparseLineSheetSetData) {
      mutate();
      clearReparseLineSheetSet();
    }
  }, [clearReparseLineSheetSet, mutate, reparseLineSheetSetData]);

  // API: Image Check Line Sheet Set
  const {
    fire: fireImageCheckLineSheetSet,
    isLoading: isImageCheckLineSheetSetLoading,
    data: imageCheckLineSheetSetData,
    error: imageCheckLineSheetSetError,
    clear: clearImageCheckLineSheetSet,
  } = useImageCheckLineSheetSet();

  useEffect(() => {
    if (imageCheckLineSheetSetError) {
      showError(imageCheckLineSheetSetError);
    }
  }, [showError, imageCheckLineSheetSetError]);

  useEffect(() => {
    if (imageCheckLineSheetSetData) {
      mutate();
      clearImageCheckLineSheetSet();
    }
  }, [clearImageCheckLineSheetSet, mutate, imageCheckLineSheetSetData]);

  // API: Apply Condition Complete Line Sheet Set
  const {
    fire: fireUpdateOrderRuleStatus,
    isLoading: isUpdateOrderRuleStatusLoading,
    data: updateOrderRuleStatusData,
    error: updateOrderRuleStatusError,
    clear: clearUpdateOrderRuleStatus,
  } = useUpdateOrderRuleStatus();

  useEffect(() => {
    if (updateOrderRuleStatusError) {
      showError(updateOrderRuleStatusError);
    }
  }, [showError, updateOrderRuleStatusError]);

  useEffect(() => {
    if (updateOrderRuleStatusData) {
      mutate();
      clearUpdateOrderRuleStatus();
    }
  }, [clearUpdateOrderRuleStatus, mutate, updateOrderRuleStatusData]);

  // END: API: Reparse Line Sheet Set

  // (부티크) 라인시트세트 상세에서는 발행사/표준화 시트 다운로드를 보여주지 않는다 —
  // 오더가 없는 세트 화면에서 이 버튼은 라인시트세트 파일을 내려받는다. 오더 화면에서는 그대로 둔다.
  const hideSetDownloadsForBoutique =
    identity?.company?.type === "BOUTIQUE" && !formik.values.orderSheetSet;

  const downloadSheetOriginAction = useMemo(() => {
    const orderSheetSet = formik.values.orderSheetSet;
    const compressedImportedFile = formik.values.compressedImportedFile;

    if (hideSetDownloadsForBoutique) {
      return undefined;
    }

    const unoFileId =
      orderSheetSet?.compressedExportedFile?.id || compressedImportedFile?.id;

    if (unoFileId) {
      return (
        <AppButton
          key={"LineSheetSetDetailAction_OrderSheetSetDownload"}
          variant={"white"}
          isLoading={findDownloadUnoFileSegment(unoFileId)?.isLoading || false}
          isDisabled={
            !orderSheetSet?.compressedExportedFile && !compressedImportedFile
          }
          onClick={() => {
            fireDownloadUnoFile(
              {
                unoFileId: unoFileId,
              },
              {}
            );
          }}
        >
          {tTitle("common:origin_download")}
        </AppButton>
      );
    }
  }, [
    findDownloadUnoFileSegment,
    fireDownloadUnoFile,
    formik.values.orderSheetSet,
    formik.values.compressedImportedFile,
    hideSetDownloadsForBoutique,
    tTitle,
  ]);

  const downloadOrderedSheetOriginAction = useMemo(() => {
    const orderSheetSet = formik.values.orderSheetSet;

    const unoFileId =
      orderSheetSet?.compressedOrderedExportedFile?.id;

    if (unoFileId) {
      return (
        <AppButton
          key={"LineSheetSetDetailAction_OrderSheetSetOrderedDownload"}
          variant={"white"}
          isLoading={findDownloadUnoFileSegment(unoFileId)?.isLoading || false}
          isDisabled={
            !orderSheetSet?.compressedExportedFile
          }
          onClick={() => {
            fireDownloadUnoFile(
              {
                unoFileId: unoFileId,
              },
              {}
            );
          }}
        >
          {tTitle("common:ordered_origin_download")}
        </AppButton>
      );
    }
  }, [findDownloadUnoFileSegment, fireDownloadUnoFile, formik.values.orderSheetSet, tTitle]);

  const downloadSheetUnoFormAction = useMemo(() => {
    const orderSheetSet = formik.values.orderSheetSet;
    const compressedUnoFormFile = formik.values.compressedUnoFormFile;

    if (hideSetDownloadsForBoutique) {
      return undefined;
    }

    const unoFileId =
      orderSheetSet?.compressedUnoFormFile?.id || compressedUnoFormFile?.id;

    if (unoFileId) {
      return (
        <AppButton
          key={"LineSheetSetDetailAction_OrderSheetSetUnoformDownload"}
          variant={"white"}
          isLoading={findDownloadUnoFileSegment(unoFileId)?.isLoading || false}
          isDisabled={
            !orderSheetSet?.compressedUnoFormFile && !compressedUnoFormFile
          }
          onClick={() => {
            fireDownloadUnoFile(
              {
                unoFileId: unoFileId,
              },
              {}
            );
          }}
        >
          {tTitle("uno_form_download")}
        </AppButton>
      );
    }
  }, [
    findDownloadUnoFileSegment,
    fireDownloadUnoFile,
    formik.values.orderSheetSet,
    formik.values.compressedUnoFormFile,
    hideSetDownloadsForBoutique,
    tTitle,
  ]);

  const downloadLineSheetSetAction = useMemo(() => {
    const unoFileId = formik.values.compressedImportedFile?.id;

    if (unoFileId) {
      return (
        <AppButton
          key={"LineSheetSetDetailAction_OriginDownload"}
          variant={"white"}
          isLoading={findDownloadUnoFileSegment(unoFileId)?.isLoading || false}
          isDisabled={!unoFileId}
          onClick={() => {
            fireDownloadUnoFile(
              {
                unoFileId: unoFileId,
              },
              {}
            );
          }}
        >
          {tTitle("origin_download")}
        </AppButton>
      );
    }
  }, [
    findDownloadUnoFileSegment,
    fireDownloadUnoFile,
    formik.values.compressedImportedFile?.id,
    tTitle,
  ]);

  const downloadLineSheetSetUnoFormAction = useMemo(() => {
    const unoFileId = formik.values.compressedUnoFormFile?.id;

    if (unoFileId) {
      return (
        <AppButton
          key={"LineSheetSetDetailAction_UnoFormDownload"}
          variant={"white"}
          isLoading={findDownloadUnoFileSegment(unoFileId)?.isLoading || false}
          isDisabled={!unoFileId}
          onClick={() => {
            fireDownloadUnoFile(
              {
                unoFileId: unoFileId,
              },
              {}
            );
          }}
        >
          {tTitle("common:uno_form_download")}
        </AppButton>
      );
    }
  }, [
    findDownloadUnoFileSegment,
    fireDownloadUnoFile,
    formik.values.compressedUnoFormFile?.id,
    tTitle,
  ]);

  const deleteOrderSheetSetAction = useMemo(() => {
    const orderSheetSetId = formik.values.orderSheetSet?.id;
    const lineSheetSetId = formik.values.id;
    const orderSheetSetStatus = formik.values.orderSheetSet?.status;
    const selectedOrderSheetIds = formik.values.lineSheetOrAttachmentList
      .filter(isOrderSheetFormField)
      .filter((i) => ["WORKING_DRAFT"].includes(i.status))
      .map((i) => i.id);

    if (
      identity?.company?.type === "BUYER" &&
      orderSheetSetId &&
      orderSheetSetStatus &&
      ["WORKING_DRAFT"].includes(orderSheetSetStatus)
    ) {
      const selectedOrderSheetStatus = formik.values.lineSheetOrAttachmentList
        .filter(isOrderSheetFormField)
        .filter((i) => i.isChecked)
        .map((i) => i.status);
      const isAllSameStatus = selectedOrderSheetStatus.every(
        (status) => status === selectedOrderSheetStatus[0]
      );
      return (
        <AppButton
          key={"LineSheetSetDetailAction_Delete"}
          variant={"white"}
          isDisabled={selectedOrderSheetIds.length === 0 || !isAllSameStatus}
          onClick={async () => {
            const result = await openDialog({
              title: tTitle("delete_order_sheet"),
              message: tTitle("common:delete_order_sheet.message"),
              confirmName: tTitle("delete.do"),
            });

            if (!result) {
              return;
            }

            fireDeleteOrderSheetSet(
              {
                buyerId: identity!.company!.id,
                orderSheetSetId,
              },
              {
                orderSheetIds: selectedOrderSheetIds,
              }
            );
            mutate();

            clearDeleteOrderSheetSet();
          }}
        >
          {tTitle("common:delete_order_sheet")}
        </AppButton>
      );
    }
  }, [
    clearDeleteOrderSheetSet,
    fireDeleteOrderSheetSet,
    formik.values.id,
    formik.values.lineSheetOrAttachmentList,
    formik.values.orderSheetSet?.id,
    formik.values.orderSheetSet?.status,
    identity,
    mutate,
    openDialog,
    tTitle,
  ]);

  const saveLineSheetSetAction = useMemo(() => {
    if (isEditing) {
      return (
        <AppButton
          key={"LineSheetSetDetailAction_Save"}
          variant={"primary"}
          isLoading={formik.isSubmitting}
          isDisabled={
            !formik.dirty ||
            Boolean(
              (formik.status as { hasInvalidInput?: boolean } | undefined)
                ?.hasInvalidInput
            )
          }
          onClick={() => {
            formik.submitForm();
          }}
        >
          {tTitle("common:save")}
        </AppButton>
      );
    }
  }, [formik, isEditing, tTitle]);

  const { isUpdateLineSheetSetQueued } = useLineSheetSetDumbTransaction();

  const editLineSheetSetAction = useMemo(() => {
    const isPublished = !!formik.values.publishedAt;
    const isDeleted = formik.values.isDeleted;
    const lineSheetSetId = formik.values.id;

    const companyType = identity?.company?.type;
    if (
      !isEditing &&
      !isPublished &&
      !isDeleted &&
      (companyType === "AGENCY" ||
        // 부티크는 자기 라인시트세트를 본다(오더를 보는 화면이 아닐 때)
        (companyType === "BOUTIQUE" && !formik.values.orderSheetSet)) &&
      lineSheetSetId
    ) {
      return (
        <AppButton
          key={"LineSheetSetDetailAction_Edit"}
          isLoading={isUpdateLineSheetSetQueued(
            companyType === "BOUTIQUE"
              ? { boutiqueId: identity!.company!.id, lineSheetSetId }
              : { agencyId: identity!.company!.id, lineSheetSetId }
          )}
          // isDisabled={props.values.lineSheetOrAttachmentList
          //   .filter(isLineSheetFormField)
          //   .some((field) => field.status === "PARSING_PENDING")}
          onClick={() => {
            navigate(`/line-sheet-sets/${lineSheetSetId}/edit`, {
              appendReferer: true,
            });
          }}
          variant={"white"}
        >
          {tTitle("edit")}
        </AppButton>
      );
    }
  }, [
    formik.values.id,
    formik.values.isDeleted,
    formik.values.orderSheetSet,
    formik.values.publishedAt,
    identity,
    isEditing,
    isUpdateLineSheetSetQueued,
    navigate,
    tTitle,
  ]);

  const copyLineSheetSetAction = useMemo(() => {
    const isDeleted = formik.values.isDeleted;
    const lineSheetSetId = formik.values.id;

    if (
      !isEditing &&
      !isDeleted &&
      identity?.company?.type === "AGENCY" &&
      lineSheetSetId
    ) {
      return (
        <AppButton
          key={"LineSheetSetDetailAction_Copy"}
          variant={"white"}
          onClick={() => {
            setIsCopyModalOpen(true);
          }}
        >
          {tTitle("common:copy")}
        </AppButton>
      );
    }
  }, [formik.values.id, formik.values.isDeleted, identity, isEditing, tTitle]);

  const forceParseFailedAction = useMemo(() => {
    const isPublished = !!formik.values.publishedAt;
    const isDeleted = formik.values.isDeleted;
    const lineSheetSetId = formik.values.id;

    if (
      identity?.company?.type === "AGENCY" &&
      !isPublished &&
      !isDeleted &&
      lineSheetSetId
    ) {
      const isAnyParsed = formik.values.lineSheetOrAttachmentList
        .filter(isLineSheetFormField)
        .filter((field) => field.type === "LINE_SHEET")
        .some((field) => field.status === "PARSED");

      if (isAnyParsed) {
        return (
          <AppButton
            key={"LineSheetSetDetailAction_ForceParseFailed"}
            variant={"white"}
            isLoading={isReportLineSheetSetLoading}
            onClick={() => {
              // 확인 모달
              openDialog({
                title: tTitle("common:force_parsing_failed"),
                message: "정말 강제 파싱 실패 처리를 하시겠습니까?",
                confirmName: tTitle("continue"),
              }).then((result) => {
                if (result) {
                  fireReportLineSheetSet(
                    {
                      agencyId: identity!.company?.id!,
                      lineSheetSetId,
                    },
                    {}
                  );
                }
              });
            }}
          >
            {tTitle("common:force_parsing_failed")}
          </AppButton>
        );
      }
    }
  }, [
    fireReportLineSheetSet,
    formik.values.id,
    formik.values.isDeleted,
    formik.values.lineSheetOrAttachmentList,
    formik.values.publishedAt,
    identity,
    isReportLineSheetSetLoading,
    openDialog,
    tTitle,
  ]);

  const reparseLineSheetSetAction = useMemo(() => {
    const isPublished = !!formik.values.publishedAt;
    const isDeleted = formik.values.isDeleted;
    const lineSheetSetId = formik.values.id;
    const status = formik.values.status;
    if (
      identity?.company?.type === "AGENCY" &&
      (status === "PARSING_PENDING" || status === "PARSING_FAILED") &&
      !isPublished &&
      !isDeleted &&
      lineSheetSetId
    ) {
      return (
        <AppButton
          key={"LineSheetSetDetailAction_Reparse"}
          isLoading={isReparseLineSheetSetLoading}
          onClick={() => {
            if (
              identity &&
              identity.company &&
              identity.company.type === "AGENCY"
            ) {
              // 확인 모달
              openDialog({
                title: tTitle("common:reparse"),
                message: "정말 파싱 재시도 하시겠습니까?",
                confirmName: tTitle("continue"),
              }).then((result) => {
                if (result) {
                  fireReparseLineSheetSet(
                    {
                      agencyId: identity!.company!.id,
                      lineSheetSetId,
                    },
                    {}
                  );
                }
              });
            }
          }}
        >
          {tTitle("reparse")}
        </AppButton>
      );
    }
  }, [
    fireReparseLineSheetSet,
    formik.values.id,
    formik.values.isDeleted,
    formik.values.publishedAt,
    formik.values.status,
    identity,
    isReparseLineSheetSetLoading,
    openDialog,
    tTitle,
  ]);

  const aiImageCheckCompleteLineSheetSetAction = useMemo(() => {
    const isPublished = !!formik.values.publishedAt;
    const isDeleted = formik.values.isDeleted;
    const lineSheetSetId = formik.values.id;
    const orderSheetSetId = formik.values.orderSheetSet?.id;
    const status = formik.values.status;
    if (
      identity?.company?.type === "AGENCY" &&
      status === "PARSING_COMPLETED" &&
      !isDeleted &&
      lineSheetSetId &&
      isPublished &&
      !orderSheetSetId
    ) {
      return (
        <AppButton
          key={"aiImageCheckCompleteLineSheetSetAction_Reparse"}
          isLoading={isImageCheckLineSheetSetLoading}
          isDisabled={formik.values.aiImageCompletedAt != null}
          onClick={() => {
            if (
              identity &&
              identity.company &&
              identity.company.type === "AGENCY"
            ) {
              // 확인 모달
              openDialog({
                title: tTitle("common:ai_image_check_complete"),
                message: "이미지 검수를 완료 처리 하시겠습니까?",
                confirmName: tTitle("continue"),
              }).then((result) => {
                if (result) {
                  fireImageCheckLineSheetSet(
                    {
                      agencyId: identity!.company!.id,
                      lineSheetSetId,
                    },
                    {}
                  );
                }
              });
            }
          }}
        >
          {tTitle("common\:ai_image_check_complete")}
        </AppButton>
      );
    }
  }, [
    fireImageCheckLineSheetSet,
    formik.values.id,
    formik.values.isDeleted,
    formik.values.publishedAt,
    formik.values.status,
    identity,
    isImageCheckLineSheetSetLoading,
    openDialog,
    tTitle,
  ]);

  const applyConditionCompleteLineSheetSetAction = useMemo(() => {
    const isPublished = !!formik.values.publishedAt;
    const isDeleted = formik.values.isDeleted;
    const lineSheetSetId = formik.values.id;
    const orderSheetSetId = formik.values.orderSheetSet?.id;
    const status = formik.values.status;
    if (
      identity?.company?.type === "AGENCY" &&
      status === "PARSING_COMPLETED" &&
      !isDeleted &&
      lineSheetSetId &&
      isPublished &&
      !orderSheetSetId
    ) {
      return (
        <AppButton
          key={"applyConditionCompleteLineSheetSetAction"}
          isLoading={isUpdateOrderRuleStatusLoading}
          onClick={() => {
            // 완료/스킵/오더룰없음 중에서 고르게 한다(기본값 적용 완료).
            setIsOrderRuleStatusModalOpen(true);
          }}
        >
          {tTitle("common:order_rule_status.update")}
        </AppButton>
      );
    }
  }, [
    formik.values.id,
    formik.values.isDeleted,
    formik.values.publishedAt,
    formik.values.status,
    identity,
    isUpdateOrderRuleStatusLoading,
    tTitle,
  ]);

  const lineSheetSetReviewStatusUpdater = useMemo(() => {
    const isPublished = !!formik.values.publishedAt;
    const isDeleted = formik.values.isDeleted;
    const lineSheetSetId = formik.values.id;

    if (
      identity?.company?.type === "AGENCY" &&
      !isPublished &&
      !isDeleted &&
      lineSheetSetId
    ) {
      return (
        <LineSheetSetReviewStatusUpdater
          key={"LineSheetSetDetailAction_ReviewStatusUpdater"}
          lineSheetSetId={lineSheetSetId}
          onUpdate={(response) => {
            mutate();
          }}
        />
      );
    }
  }, [
    formik.values.id,
    formik.values.isDeleted,
    formik.values.publishedAt,
    identity?.company?.type,
    mutate,
  ]);

  const publishLineSheetSetAction = useMemo(() => {
    const isPublished = !!formik.values.publishedAt;
    const isDeleted = formik.values.isDeleted;
    const lineSheetSetId = formik.values.id;
    const status = formik.values.status;
    const compressionStatus = formik.values.compressionStatus;

    if (
      !isEditing &&
      !isDeleted &&
      !isPublished &&
      lineSheetSetId &&
      identity?.company?.type === "AGENCY"
    ) {
      return (
        <AppButton
          key={"LineSheetSetDetailAction_Publish"}
          variant={"primary"}
          isDisabled={
            status === undefined ||
            status === "PARSING_PENDING" ||
            compressionStatus !== "COMPLETED"
          }
          onClick={() => {
            onPublishClick();
          }}
        >
          {tTitle("publish")}
        </AppButton>
      );
    }
  }, [
    formik.values.id,
    formik.values.isDeleted,
    formik.values.publishedAt,
    formik.values.status,
    formik.values.compressionStatus,
    identity?.company?.type,
    isEditing,
    onPublishClick,
    tTitle,
  ]);

  const updateOrderSheetStatusItem = useMemo(():
    | UpdateOrderSheetStatusItem[]
    | UpdateOrderSheetSetStatusItem[] => {
    const orderSheetSet = formik.values.orderSheetSet;
    const issuedByBoutique = formik.values.issuedByBoutique;
    const lineSheetOrAttachmentList = formik.values.lineSheetOrAttachmentList;
    const orderSheets = formik.values.lineSheetOrAttachmentList.filter(isOrderSheetFormField)

    const orderSheetSetItems = (orderSheetSet ? [orderSheetSet] : [])
      .map((row): UpdateOrderSheetSetStatusItem => {
        return {
          id: row.id,
          status: row.status,
          currency: formik.values.currency,
          orderSheets: orderSheets
            .map((orderSheetRow) => {
              return {
                orderedAmount: orderSheetRow.orderedAmount,
                orderedQuantity: orderSheetRow.orderedQuantity,
                id: orderSheetRow.id,
                name: orderSheetRow.lineSheet.name,
                confirmedAmount: orderSheetRow.confirmedAmount,
                confirmedQuantity: orderSheetRow.confirmedQuantity,
                issuedBy: {
                  name: issuedByBoutique?.name || "",
                },
                lineSheet: {
                  id: orderSheetRow.lineSheet.id,
                  status: orderSheetRow.lineSheet.status,
                },
                toBeSubmitted: orderSheetRow.toBeSubmitted,
              };
            }),
          createdByCompany: {
            name: row.createdByCompany.name,
          },
        };
      });
    return orderSheetSetItems;
  }, [
    formik.values.issuedByBoutique,
    formik.values.lineSheetOrAttachmentList,
    formik.values.orderSheetSet,
  ]);

  const orderSheetStatusUpdater = useMemo(() => {
    const orderSheetSet = formik.values.orderSheetSet;

    if (
      (identity?.company?.type === "AGENCY" ||
        identity?.company?.type === "BOUTIQUE") &&
      orderSheetSet
    ) {
      return (
        <OrderSheetStatusUpdater
          key={"LineSheetSetDetailAction_OrderSheetStatusUpdater"}
          items={updateOrderSheetStatusItem}
          onUpdate={() => {
            mutate();
          }}
          isBoutique={identity?.company?.type === "BOUTIQUE"}
        />
      );
    }
  }, [
    formik.values.orderSheetSet,
    identity?.company?.type,
    mutate,
    updateOrderSheetStatusItem,
  ]);

  const selectedOrderSheetIds = useMemo(() => {
    return formik.values.lineSheetOrAttachmentList
      .filter(isOrderSheetFormField)
      .filter(
        (i) =>
          ["WORKING_DRAFT", "RESUBMIT_REQUIRED"].includes(i.status)
      )
      .map((i) => i.id);
  }, [formik.values.lineSheetOrAttachmentList]);

  const {
    isOpen: isOrderOrderSheetSetModalOpen,
    onOpen: onOpenOrderOrderSheetSetModal,
    onClose: onCloseOrderOrderSheetSetModal,
    data: orderOrderSheetSetModalData,
    openWithData: openOrderOrderSheetSetModalWithData,
  } = useDisclosureWithData<OrderOrderSheetSetModalProps>();

  const orderAction = useMemo(() => {
    if (
      identity?.company?.type === "BUYER" &&
      formik.values.orderSheetSet?.orderSheets.some(
        (i) => i.status === "WORKING_DRAFT" || i.status === "RESUBMIT_REQUIRED"
      )
    ) {
      const selectedOrderSheetStatus = formik.values.lineSheetOrAttachmentList
        .filter(isOrderSheetFormField)
        .filter((i) => i.isChecked)
        .map((i) => i.status);
      const isAllSameStatus = selectedOrderSheetStatus.every(
        (status) => status === selectedOrderSheetStatus[0]
      );
      // if (
      //   isAllSameStatus &&
      //   selectedOrderSheetStatus[0] === "ORDERED_TO_AGENCY"
      // ) {
      //   return;
      // }
      return (
        <AppButton
          key={"LineSheetSetDetailAction_Order"}
          variant={"primary"}
          onClick={() => {
            openOrderOrderSheetSetModalWithData((prev) => {
              return {
                orderSheetSetId: formik.values.orderSheetSet!.id,
                orderSheetIds: selectedOrderSheetIds,
                onSubmit: () => {
                  mutate();
                  mutateIfMatched(
                    new RegExp(
                      `^(?:/)(buyers|agencies|boutiques)/(\\d+)/dense-?order-sheet-sets/${
                        formik.values.orderSheetSet!.id
                      }$`
                    )
                  );
                },
              };
            });
          }}
          disabledMessage={tSentence(
            "order_sheet_sets:disabled_button_to_submit_order_sheet.message"
          )}
        >
          {tTitle("submit")}
        </AppButton>
      );
    }
  }, [
    formik.values.lineSheetOrAttachmentList,
    formik.values.orderSheetSet,
    identity?.company?.type,
    mutate,
    openOrderOrderSheetSetModalWithData,
    selectedOrderSheetIds,
    tSentence,
    tTitle,
  ]);

  // (부티크) 모든 바이어 주문을 상품별로 합쳐 보는 화면으로
  const allOrderedProductsAction = useMemo(() => {
    const lineSheetSetId = formik.values.id;
    if (
      !isEditing &&
      identity?.company?.type === "BOUTIQUE" &&
      lineSheetSetId &&
      !formik.values.orderSheetSet
    ) {
      return (
        <AppButton
          key={"LineSheetSetDetailAction_AllOrderedProducts"}
          variant={"white"}
          onClick={() => {
            navigate(`/line-sheet-sets/${lineSheetSetId}/ordered-products`, {
              appendReferer: true,
            });
          }}
        >
          {tTitle("all_ordered_products.title")}
        </AppButton>
      );
    }
  }, [
    formik.values.id,
    formik.values.orderSheetSet,
    identity,
    isEditing,
    navigate,
    tTitle,
  ]);

  // (부티크) 모든 오더의 확정 수량을 다 넣었으면 라인시트세트의 오더를 한 번에 부티크 확정(바이어 승인 필요)
  const isBoutiqueSetView =
    !isEditing &&
    identity?.company?.type === "BOUTIQUE" &&
    !!formik.values.id &&
    !formik.values.orderSheetSet;
  const { data: confirmReadiness, mutate: mutateConfirmReadiness } =
    useBoutiqueConfirmReadiness(
      isBoutiqueSetView && identity?.company && formik.values.id
        ? { boutiqueId: identity.company.id, lineSheetSetId: formik.values.id }
        : null,
      { revalidateOnFocus: false }
    );
  const { fire: fireConfirmAllOrders, isLoading: isConfirmAllOrdersLoading } =
    useBoutiqueConfirmAllOrders();
  const { mutate: globalMutate } = useSWRConfig();

  const confirmAllOrdersAction = useMemo(() => {
    const lineSheetSetId = formik.values.id;
    if (
      !isBoutiqueSetView ||
      !identity?.company ||
      !lineSheetSetId ||
      !confirmReadiness ||
      confirmReadiness.totalCount === 0
    ) {
      return undefined;
    }
    const boutiqueId = identity.company.id;
    const { readyCount, totalCount, allReady } = confirmReadiness;
    return (
      <AppButton
        key={"LineSheetSetDetailAction_BoutiqueConfirmAllOrders"}
        variant={"primary"}
        isLoading={isConfirmAllOrdersLoading}
        isDisabled={!allReady || isConfirmAllOrdersLoading}
        onClick={async () => {
          const result = await openDialog({
            title: tTitle("boutique_confirm_all.title"),
            message: tTitle("boutique_confirm_all.message"),
            confirmName: tTitle("boutique_confirm_all.confirm"),
          });
          if (!result) {
            return;
          }
          try {
            const response = await fireConfirmAllOrders(
              { boutiqueId, lineSheetSetId },
              {},
              { rethrow: true }
            );
            const failed: BoutiqueConfirmAllOrdersResponse["failed"] =
              response?.failed ?? [];
            if (failed.length) {
              // 한 건이 실패해도 나머지 오더는 확정된다 — 실패한 오더만 알려준다
              showError(failed.map((f) => `#${f.id} ${f.message}`).join("\n"));
            } else {
              showInfo(tTitle("boutique_confirm_all.done"));
            }
          } catch (e) {
            showError(e);
          } finally {
            mutate();
            mutateConfirmReadiness();
            // 라인시트 카드 안 오더 목록 등 이 화면의 다른 목록도 새 상태로 다시 받는다
            globalMutate(() => true);
          }
        }}
      >
        {`${tTitle("boutique_confirm_all.button")} (${readyCount}/${totalCount})`}
      </AppButton>
    );
  }, [
    confirmReadiness,
    fireConfirmAllOrders,
    formik.values.id,
    globalMutate,
    identity,
    isBoutiqueSetView,
    isConfirmAllOrdersLoading,
    mutate,
    mutateConfirmReadiness,
    openDialog,
    showError,
    showInfo,
    tTitle,
  ]);

  const actions = useMemo(() => {
    return [
      saveLineSheetSetAction,
      editLineSheetSetAction,
      copyLineSheetSetAction,
      forceParseFailedAction,
      reparseLineSheetSetAction,
      // lineSheetSetReviewStatusUpdater,
      publishLineSheetSetAction,
      allOrderedProductsAction,
      confirmAllOrdersAction,
      downloadSheetOriginAction,
      downloadOrderedSheetOriginAction,
      downloadSheetUnoFormAction,
      deleteOrderSheetSetAction,
      orderSheetStatusUpdater,
      orderAction,
      aiImageCheckCompleteLineSheetSetAction,
      applyConditionCompleteLineSheetSetAction,
    ];
    //
  }, [
    allOrderedProductsAction,
    confirmAllOrdersAction,
    deleteOrderSheetSetAction,
    downloadSheetOriginAction,
    downloadOrderedSheetOriginAction,
    downloadSheetUnoFormAction,
    editLineSheetSetAction,
    copyLineSheetSetAction,
    forceParseFailedAction,
    // lineSheetSetReviewStatusUpdater,
    orderAction,
    orderSheetStatusUpdater,
    publishLineSheetSetAction,
    reparseLineSheetSetAction,
    saveLineSheetSetAction,
    aiImageCheckCompleteLineSheetSetAction,
    applyConditionCompleteLineSheetSetAction,
  ]);

  return (
    <>
      <AppListHeader
        filters={[
        ]}
        showFilterText={false}
        actions={actions}
        showNavigateToBack={true}
        blockNavigateToBack={formik.dirty && isEditing}
        emptyStackPage={emptyStackPage}
      />
      <AppListHeader
        filters={[
          <AppQueryController
            key={"AgencyLineSheetSetListFilter_Query"}
            value={query.query}
            showButton={false}
            placeholder={tTitle("search_input_placeholder")}
            leadingIcon="search"
            onChange={(value) =>
              setQuery((prev) => {
                return {
                  ...prev,
                  query: value,
                  pageNumber: 1,
                };
              })
            }
          />,
          <Button
            key={"FilterButton"}
            padding={"4px 8px"}
            height="28px"
            display="flex"
            gap={"4px"}
            variant={"outline"}
            onClick={() => setIsFilterModalOpen(true)}
            minWidth={"82px"}
            border={`${
              queryCount > 0 ? "1px solid #1272EF" : "1px solid #8F8F8C"
            }`}
          >
            <SVG
              src={"/icons-v1_1/filter.svg"}
              width={"16px"}
              height={"16px"}
              color={"#444444"}
              style={{
                minWidth: "16px",
              }}
            />
            <Text textStyle={"body-sm-bold"}>
              {tTitle("filter")} {queryCount > 0 && queryCount}
            </Text>
          </Button>,
          () => {
            if (queryCount <= 0) return;
            return (
              <AppButton
                key={"AgencyLineSheetSetListFilter_ResetFilter"}
                variant={"ghost"}
                size={"medium"}
                onClick={() => {
                  setQuery({
                    pageNumber: 1,
                  });
                }}
                width={"72px"}
                height={"28px"}
              >
                <Flex gap={"4px"} alignItems={"center"}>
                  <SVG
                    src={"/icons-v1_1/arrow-rotate.svg"}
                    width={"16px"}
                    height={"16px"}
                    color={"#8F8F8C"}
                  />
                  <Text textStyle="body-sm-bold" color={"#444444"}>
                    {t("clear_all")}
                  </Text>
                </Flex>
              </AppButton>
            );
          },
          () => {
            if (queryCount <= 0) return;
            return (
              <Text key={"Divider"} color={"#D4D4D1"}>
                |
              </Text>
            );
          },
          <ChipList key={"ChipList"} query={query} setQuery={setQuery} />,
        ]}
        showFilterText={false}
        showNavigateToBack={false}
      />
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        queryCount={queryCount}
        setQueryCount={(count) => setQueryCount(count)}
        query={query}
        setQuery={setQuery}
        resetBtn={
          <AppButton
            key={"AgencyLineSheetSetListFilter_ResetFilter"}
            variant={"ghost"}
            size={"medium"}
            onClick={() => {
              setQuery({
                pageNumber: 1,
              });
            }}
            width={"72px"}
            height={"28px"}
          >
            <Flex gap={"4px"} alignItems={"center"}>
              <SVG
                src={"/icons-v1_1/arrow-rotate.svg"}
                width={"16px"}
                height={"16px"}
                color={"#8F8F8C"}
              />
              <Text textStyle="body-sm-bold" color={"#444444"}>
                {tTitle("clear_all")}
              </Text>
            </Flex>
          </AppButton>
        }
      >
        <Box>
          <Flex flexDirection="column" gap={"24px"}>
            <Flex flexDirection={"column"} gap={"6px"}>
              <Text>{tTitle("brand")}</Text>
              <AppCompanySelect
                key={"AgencyLineSheetSetListFilter_Brand"}
                name={tTitle("common:brand")}
                types={["BRAND"]}
                id={query["madeByCompany.id__in"]}
                isMultiple={true}
                onSelect={(companies) => {
                  setQuery((prev) => {
                    return {
                      ...prev,
                      "madeByCompany.id__in": companies?.map(
                        (company) => company.id
                      ),
                      pageNumber: 1,
                    };
                  });
                }}
              />
            </Flex>
          </Flex>
        </Box>
      </FilterModal>
      {orderOrderSheetSetModalData && (
        <OrderOrderSheetSetModal
          {...orderOrderSheetSetModalData}
          isOpen={isOrderOrderSheetSetModalOpen}
          onClose={onCloseOrderOrderSheetSetModal}
        />
      )}
      {identity?.company?.type === "AGENCY" && formik.values.id && (
        <OrderRuleStatusModal
          isOpen={isOrderRuleStatusModalOpen}
          onClose={() => setIsOrderRuleStatusModalOpen(false)}
          agencyId={identity.company.id}
          lineSheetSetId={formik.values.id}
          currentStatus={formik.values.orderRuleStatus}
          mutate={mutate}
        />
      )}
      {isCopyModalOpen && formik.values.id && (
        <CopyLineSheetSetModal
          isOpen={isCopyModalOpen}
          onClose={() => setIsCopyModalOpen(false)}
          lineSheetSetId={formik.values.id}
          lineSheetSetName={formik.values.name}
          onCopied={(response) => {
            navigate(`/line-sheet-sets/${response.id}`, {
              appendReferer: true,
            });
          }}
        />
      )}
    </>
  );
}
